import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSpecialistAuth } from '../../context/SpecialistAuthContext';
import SpecialistDashboardLayout from '../../components/SpecialistDashboardLayout';
import { API_URL, SOCKET_URL } from '../../config/api';
import './SpecialistCreateProjectPage.css';

// Helper to resolve image URLs (relative paths → full backend URL)
const getImageUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('data:')) return url;
  if (url.startsWith('/')) return `${SOCKET_URL}${url}`;
  return url;
};

const SpecialistCreateProjectPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { specialist } = useSpecialistAuth();

  // Check if specialist's plan allows project submission
  // Plan slugs: 'basic', 'gcc-business', 'global-enterprise'
  const planName = (specialist?.membershipPlan || 'basic').toLowerCase();
  const canSubmitProjects = planName.includes('gcc') || planName.includes('business') || planName.includes('global') || planName.includes('enterprise') || planName === 'professional';

  // Check if editing (project data passed via state)
  const editingProject = location.state?.project || null;

  const [wizardStep, setWizardStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Step 1 form data
  const [category, setCategory] = useState('');
  const [subCategory, setSubCategory] = useState('');
  const [projectTitle, setProjectTitle] = useState('');
  const [subTitle, setSubTitle] = useState('');
  const [extraFields, setExtraFields] = useState([{ title: '', value: '' }]);

  // Main image
  const [mainImageFile, setMainImageFile] = useState(null);
  const [mainImageName, setMainImageName] = useState('');

  // Project images (max 10)
  const [projectImages, setProjectImages] = useState([
    { file: null, name: '' },
    { file: null, name: '' },
    { file: null, name: '' },
    { file: null, name: '' },
    { file: null, name: '' },
    { file: null, name: '' },
    { file: null, name: '' }
  ]);

  // Gallery images (max 16)
  const [galleryImages, setGalleryImages] = useState([
    { file: null, name: '' },
    { file: null, name: '' },
    { file: null, name: '' },
    { file: null, name: '' },
    { file: null, name: '' },
    { file: null, name: '' },
    { file: null, name: '' },
    { file: null, name: '' },
    { file: null, name: '' },
    { file: null, name: '' }
  ]);

  // Upload refs
  const mainImageRef = React.useRef(null);
  const projectUploadRef = React.useRef(null);
  const galleryUploadRef = React.useRef(null);

  // Sub-category options based on category
  const subCategoryOptions = {
    'Building Materials': ['Cement', 'Steel', 'Concrete', 'Bricks', 'Glass', 'Timber', 'Paint', 'Tiles'],
    'Infrastructure': ['Roads', 'Bridges', 'Tunnels', 'Railways', 'Airports', 'Ports'],
    'Residential': ['Apartments', 'Villas', 'Townhouses', 'Condominiums'],
    'Commercial': ['Office Buildings', 'Shopping Malls', 'Hotels', 'Warehouses'],
    'Industrial': ['Factories', 'Plants', 'Refineries', 'Power Stations'],
    'Hospitality': ['Hotels', 'Resorts', 'Restaurants', 'Spas'],
    'Healthcare': ['Hospitals', 'Clinics', 'Labs', 'Pharmacies'],
    'Educational': ['Schools', 'Universities', 'Training Centers', 'Libraries'],
    'Retail': ['Shops', 'Showrooms', 'Markets', 'Malls'],
    'Mixed Use': ['Residential + Commercial', 'Office + Retail', 'Hotel + Residential'],
    'Government': ['Government Buildings', 'Military', 'Public Facilities', 'Monuments']
  };

  // Pre-fill when editing
  useEffect(() => {
    if (editingProject) {
      setCategory(editingProject.projectType || '');
      setProjectTitle(editingProject.title || '');
      setSubTitle(editingProject.description?.substring(0, 100) || '');
      if (editingProject.image) {
        setMainImageName(editingProject.image.split('/').pop() || 'image.png');
      }
    }
  }, [editingProject]);

  // Handlers for extra fields
  const addExtraField = () => {
    setExtraFields(prev => [...prev, { title: '', value: '' }]);
  };

  const removeExtraField = (index) => {
    setExtraFields(prev => prev.filter((_, i) => i !== index));
  };

  const updateExtraField = (index, key, val) => {
    setExtraFields(prev => prev.map((f, i) => i === index ? { ...f, [key]: val } : f));
  };

  // File handlers
  const handleMainImageSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setMainImageFile(file);
      setMainImageName(file.name);
    }
  };

  const handleProjectImageFile = (index, e) => {
    const file = e.target.files[0];
    if (file) {
      setProjectImages(prev => prev.map((img, i) => i === index ? { file, name: file.name } : img));
    }
  };

  const removeProjectImage = (index) => {
    setProjectImages(prev => prev.map((img, i) => i === index ? { file: null, name: '' } : img));
  };

  const handleGalleryImageFile = (index, e) => {
    const file = e.target.files[0];
    if (file) {
      setGalleryImages(prev => prev.map((img, i) => i === index ? { file, name: file.name } : img));
    }
  };

  const removeGalleryImage = (index) => {
    setGalleryImages(prev => prev.map((img, i) => i === index ? { file: null, name: '' } : img));
  };

  // Bulk upload handler
  const handleBulkProjectUpload = (e) => {
    const files = Array.from(e.target.files);
    setProjectImages(prev => {
      const updated = [...prev];
      let idx = 0;
      for (let i = 0; i < updated.length && idx < files.length; i++) {
        if (!updated[i].name) {
          updated[i] = { file: files[idx], name: files[idx].name };
          idx++;
        }
      }
      // Add more slots if needed
      while (idx < files.length && updated.length < 10) {
        updated.push({ file: files[idx], name: files[idx].name });
        idx++;
      }
      return updated;
    });
  };

  const handleBulkGalleryUpload = (e) => {
    const files = Array.from(e.target.files);
    setGalleryImages(prev => {
      const updated = [...prev];
      let idx = 0;
      for (let i = 0; i < updated.length && idx < files.length; i++) {
        if (!updated[i].name) {
          updated[i] = { file: files[idx], name: files[idx].name };
          idx++;
        }
      }
      while (idx < files.length && updated.length < 16) {
        updated.push({ file: files[idx], name: files[idx].name });
        idx++;
      }
      return updated;
    });
  };

  // Next / Submit handlers
  const handleNext = () => {
    if (wizardStep === 1) {
      if (!projectTitle.trim()) {
        setError('Project Title is required.');
        return;
      }
      setError('');
      setWizardStep(2);
    }
  };

  const handleSubmit = async () => {
    if (!projectTitle.trim()) {
      setError('Project Title is required.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const token = localStorage.getItem('specialist_token');

      // Helper to upload a single image file
      const uploadSingleImage = async (file) => {
        const formData = new FormData();
        formData.append('image', file);
        const res = await fetch(`${API_URL}/upload/image?type=images`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body: formData
        });
        if (!res.ok) throw new Error('Failed to upload image');
        const data = await res.json();
        return data.imageUrl; // e.g. /uploads/images/xxx.jpg
      };

      // Helper to upload multiple image files (max 10 per call)
      const uploadMultipleImages = async (files) => {
        if (files.length === 0) return [];
        const formData = new FormData();
        files.forEach(f => formData.append('images', f));
        const res = await fetch(`${API_URL}/upload/images?type=images`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body: formData
        });
        if (!res.ok) throw new Error('Failed to upload images');
        const data = await res.json();
        return data.images.map(img => img.imageUrl);
      };

      // 1) Upload main image
      let mainImageUrl = editingProject?.image || '';
      if (mainImageFile) {
        mainImageUrl = await uploadSingleImage(mainImageFile);
      }

      // 2) Upload project images
      const projectFiles = projectImages.filter(img => img.file).map(img => img.file);
      const projectImageUrls = await uploadMultipleImages(projectFiles);

      // 3) Upload gallery images (split into batches of 10 if needed)
      const galleryFiles = galleryImages.filter(img => img.file).map(img => img.file);
      let galleryImageUrls = [];
      for (let i = 0; i < galleryFiles.length; i += 10) {
        const batch = galleryFiles.slice(i, i + 10);
        const urls = await uploadMultipleImages(batch);
        galleryImageUrls.push(...urls);
      }

      // Combine all additional images
      const allImages = [...projectImageUrls, ...galleryImageUrls];

      const payload = {
        title: projectTitle,
        description: subTitle || projectTitle,
        projectType: category,
        image: mainImageUrl,
        images: allImages.length > 0 ? allImages : undefined,
        status: 'active'
      };

      if (editingProject) {
        const res = await fetch(`${API_URL}/projects/${editingProject.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify(payload)
        });
        if (!res.ok) { const d = await res.json(); throw new Error(d.error || 'Failed to update'); }
      } else {
        const res = await fetch(`${API_URL}/projects/specialist`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify(payload)
        });
        if (!res.ok) { const d = await res.json(); throw new Error(d.error || 'Failed to create'); }
      }
      navigate('/specialist-projects');
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  // File chooser icon SVG
  const FileImageIcon = () => (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M6.63 8.138L3.249 12.418C3.057 12.658 3.249 12.998 3.558 12.998H10.299C10.619 12.998 10.801 12.658 10.609 12.418L7.249 8.138C7.099 7.938 6.779 7.938 6.63 8.138Z" fill="#969696"/>
      <path d="M10.371 10.09L8.344 12.66C8.227 12.8 8.344 13.01 8.525 13.01H12.568C12.76 13.01 12.867 12.81 12.749 12.66L10.733 10.09C10.712 10.062 10.684 10.04 10.652 10.024C10.619 10.008 10.583 10 10.547 10C10.51 10 10.474 10.008 10.442 10.024C10.409 10.04 10.381 10.062 10.36 10.09H10.371Z" fill="#969696"/>
      <path d="M10.668 9C11.257 9 11.735 8.552 11.735 8C11.735 7.448 11.257 7 10.668 7C10.079 7 9.602 7.448 9.602 8C9.602 8.552 10.079 9 10.668 9Z" fill="#969696"/>
      <path d="M13.329 16H2.663C1.777 16 1.063 15.33 1.063 14.5V1.5C1.063 0.67 1.777 0 2.663 0H10.225C10.652 0 11.057 0.16 11.356 0.44L14.46 3.35C14.759 3.63 14.929 4.01 14.929 4.41V14.5C14.929 15.33 14.215 16 13.329 16ZM2.663 1C2.364 1 2.129 1.22 2.129 1.5V14.5C2.129 14.78 2.364 15 2.663 15H13.329C13.628 15 13.863 14.78 13.863 14.5V4.41C13.863 4.344 13.85 4.279 13.822 4.219C13.794 4.159 13.754 4.105 13.703 4.06L10.599 1.15C10.5 1.056 10.366 1.002 10.225 1H2.663Z" fill="#969696"/>
      <path d="M14.274 5.001H11.17C10.306 5.001 9.602 4.341 9.602 3.531V0.621C9.602 0.341 9.836 0.121 10.135 0.121C10.434 0.121 10.668 0.341 10.668 0.621V3.531C10.668 3.791 10.892 4.001 11.17 4.001H14.274C14.572 4.001 14.807 4.221 14.807 4.501C14.807 4.781 14.572 5.001 14.274 5.001Z" fill="#969696"/>
    </svg>
  );

  // Delete icon SVG
  const DeleteIcon = ({ onClick }) => (
    <button className="scp-file-delete-btn" onClick={onClick} type="button">
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect width="20" height="20" rx="4" fill="#FFDEDE"/>
        <path d="M7.084 15.25C6.763 15.25 6.488 15.136 6.26 14.908C6.032 14.679 5.917 14.405 5.917 14.083V6.5H5.334V5.333H8.25V4.75H11.75V5.333H14.667V6.5H14.084V14.083C14.084 14.404 13.969 14.679 13.741 14.908C13.513 15.136 13.238 15.25 12.917 15.25H7.084ZM12.917 6.5H7.084V14.083H12.917V6.5ZM8.25 12.917H9.417V7.667H8.25V12.917ZM10.584 12.917H11.75V7.667H10.584V12.917Z" fill="#FF0000"/>
      </svg>
    </button>
  );

  // File chooser row component with optional preview
  const FileRow = ({ name, onDelete, onChoose, file, existingUrl }) => {
    const [preview, setPreview] = React.useState('');
    React.useEffect(() => {
      if (file) {
        const url = URL.createObjectURL(file);
        setPreview(url);
        return () => URL.revokeObjectURL(url);
      } else if (existingUrl) {
        setPreview(getImageUrl(existingUrl));
      } else {
        setPreview('');
      }
    }, [file, existingUrl]);

    return (
      <div className="scp-file-row">
        {preview && (
          <img src={preview} alt="preview" className="scp-file-preview-thumb" />
        )}
        <div className="scp-file-tag" onClick={onChoose}>
          <FileImageIcon />
          <span className="scp-file-name">{name || 'Choose file.png'}</span>
        </div>
        <DeleteIcon onClick={onDelete} />
      </div>
    );
  };

  // Upload section component
  const UploadSection = ({ onUploadClick }) => (
    <div className="scp-upload-section">
      <div className="scp-upload-label">Upload</div>
      <div className="scp-upload-btn-row">
        <button type="button" className="scp-upload-choose-btn" onClick={onUploadClick}>
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M7.335 10.664V5.231L5.601 6.964L4.668 5.997L8.001 2.664L11.335 5.997L10.401 6.964L8.668 5.231V10.664H7.335ZM4.001 13.331C3.635 13.331 3.321 13.2 3.06 12.939C2.799 12.679 2.668 12.365 2.668 11.997V9.997H4.001V11.997H12.001V9.997H13.335V11.997C13.335 12.364 13.204 12.678 12.943 12.939C12.682 13.201 12.368 13.331 12.001 13.331H4.001Z" fill="#333333"/>
          </svg>
          <span>Choose file</span>
        </button>
        <span className="scp-upload-text">Upload any attachment</span>
      </div>
      <div className="scp-upload-hint">Upload any file type. Maximum file size: 64MB (Image Size: 1600x150)</div>
    </div>
  );

  // If specialist's plan doesn't allow project submission, show upgrade message
  if (!canSubmitProjects) {
    return (
      <SpecialistDashboardLayout>
        <div className="scp-page">
          <div className="scp-header">
            <h1 className="scp-title">Project Listing</h1>
          </div>
          <div style={{
            textAlign: 'center', padding: '60px 20px',
            background: '#fff', borderRadius: '12px', margin: '30px 0'
          }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>🔒</div>
            <h2 style={{ color: '#1F396C', marginBottom: '12px' }}>Upgrade Required</h2>
            <p style={{ color: '#666', maxWidth: '500px', margin: '0 auto 24px' }}>
              Submitting projects requires a Professional or Enterprise plan.
              Upgrade your membership to showcase your projects.
            </p>
            <button
              onClick={() => navigate('/specialist-membership')}
              style={{
                background: '#1F396C', color: '#fff', border: 'none',
                padding: '12px 32px', borderRadius: '8px', cursor: 'pointer',
                fontSize: '16px', fontWeight: '600'
              }}
            >
              View Membership Plans
            </button>
          </div>
        </div>
      </SpecialistDashboardLayout>
    );
  }

  return (
    <SpecialistDashboardLayout>
      <div className="scp-page">
        {/* Page Header */}
        <div className="scp-header">
          <h1 className="scp-title">Project Listing</h1>
          <button className="scp-edit-btn" onClick={() => navigate('/specialist-projects')}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 20H21" stroke="#2B2B2B" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M16.5 3.5C16.898 3.102 17.437 2.879 18 2.879C18.279 2.879 18.554 2.934 18.812 3.04C19.069 3.147 19.303 3.303 19.5 3.5C19.697 3.697 19.853 3.931 19.96 4.188C20.067 4.446 20.121 4.722 20.121 5C20.121 5.279 20.067 5.555 19.96 5.812C19.853 6.069 19.697 6.303 19.5 6.5L7 19L3 20L4 16L16.5 3.5Z" stroke="#2B2B2B" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <span>Edit</span>
          </button>
        </div>

        {/* Step Indicator */}
        <div className="scp-steps-wrapper">
          <div className="scp-steps-circles">
            {/* Step 1 */}
            <div className="scp-step-circle-wrap">
              <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="20" cy="20" r="19.5" stroke={wizardStep >= 1 ? '#1F396C' : '#BFBFBF'} strokeDasharray="5.11 5.11"/>
                <circle cx="20" cy="20" r="14.5" fill={wizardStep >= 1 ? '#1F396C' : '#BFBFBF'} stroke="white" strokeWidth="0.85"/>
                {wizardStep > 1 ? (
                  <path d="M27.235 15.321L17.288 24.683L12.767 20.427" stroke="white" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"/>
                ) : null}
              </svg>
            </div>

            {/* Line 1 */}
            <div className="scp-step-line" style={{ background: wizardStep > 1 ? '#1F396C' : '#BFBFBF' }}></div>

            {/* Step 2 */}
            <div className="scp-step-circle-wrap">
              <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="20" cy="20" r="19.5" stroke={wizardStep >= 2 ? '#1F396C' : '#BFBFBF'} strokeDasharray="5.11 5.11"/>
                <circle cx="20" cy="20" r="14.5" fill={wizardStep >= 2 ? '#1F396C' : '#BFBFBF'} stroke="white" strokeWidth="0.85"/>
                {wizardStep > 2 ? (
                  <path d="M27.235 15.321L17.288 24.683L12.767 20.427" stroke="white" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"/>
                ) : null}
              </svg>
            </div>

            {/* Line 2 */}
            <div className="scp-step-line" style={{ background: wizardStep > 2 ? '#1F396C' : '#BFBFBF' }}></div>

            {/* Step 3 (Publish) */}
            <div className="scp-step-circle-wrap">
              <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="20" cy="20" r="19.5" stroke={wizardStep >= 3 ? '#1F396C' : '#BFBFBF'} strokeDasharray="5.11 5.11"/>
                <circle cx="20" cy="20" r="14.5" fill={wizardStep >= 3 ? '#1F396C' : '#BFBFBF'} stroke="white" strokeWidth="0.85"/>
              </svg>
            </div>
          </div>
          <div className="scp-steps-labels">
            <span className={`scp-step-label ${wizardStep >= 1 ? 'active' : ''}`}>1</span>
            <span className={`scp-step-label ${wizardStep >= 2 ? 'active' : ''}`}>2</span>
            <span className={`scp-step-label ${wizardStep >= 3 ? 'active' : ''}`}>Publish</span>
          </div>
        </div>

        {error && <div className="scp-error">{error}</div>}

        {/* ===== STEP 1 ===== */}
        {wizardStep === 1 && (
          <div className="scp-form-sections">
            {/* === Section: Basic Information (Category) === */}
            <div className="scp-section">
              <h3 className="scp-section-heading">Basic Information</h3>
              <div className="scp-card">
                <div className="scp-row">
                  <div className="scp-field">
                    <label className="scp-label">Category</label>
                    <div className="scp-select-wrapper">
                      <select value={category} onChange={(e) => { setCategory(e.target.value); setSubCategory(''); }}>
                        <option value="">Building Materials</option>
                        <option value="Building Materials">Building Materials</option>
                        <option value="Infrastructure">Infrastructure</option>
                        <option value="Residential">Residential</option>
                        <option value="Commercial">Commercial</option>
                        <option value="Industrial">Industrial</option>
                        <option value="Hospitality">Hospitality</option>
                        <option value="Healthcare">Healthcare</option>
                        <option value="Educational">Educational</option>
                        <option value="Retail">Retail</option>
                        <option value="Mixed Use">Mixed Use</option>
                        <option value="Government">Government</option>
                      </select>
                      <svg className="scp-select-chevron" width="24" height="24" viewBox="0 0 24 24" fill="none">
                        <path d="M6 9L12 15L18 9" stroke="#6D6D6D" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </div>
                  </div>
                  <div className="scp-field">
                    <label className="scp-label">Sub category</label>
                    <div className="scp-select-wrapper">
                      <select value={subCategory} onChange={(e) => setSubCategory(e.target.value)}>
                        <option value="">Cement</option>
                        {(subCategoryOptions[category] || subCategoryOptions['Building Materials']).map(opt => (
                          <option key={opt} value={opt}>{opt}</option>
                        ))}
                      </select>
                      <svg className="scp-select-chevron" width="24" height="24" viewBox="0 0 24 24" fill="none">
                        <path d="M6 9L12 15L18 9" stroke="#6D6D6D" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* === Section: Basic Information (Title + Extras) === */}
            <div className="scp-section">
              <h3 className="scp-section-heading">Basic Information</h3>
              <div className="scp-card">
                {/* Project Title */}
                <div className="scp-field scp-field-full">
                  <label className="scp-label">Project Title</label>
                  <input
                    type="text"
                    className="scp-input"
                    placeholder="Project Title"
                    value={projectTitle}
                    onChange={(e) => setProjectTitle(e.target.value)}
                  />
                </div>

                {/* Sub Title */}
                <div className="scp-field scp-field-full" style={{ marginTop: '24px' }}>
                  <label className="scp-label">Sub Title</label>
                  <input
                    type="text"
                    className="scp-input"
                    placeholder="Sub Title"
                    value={subTitle}
                    onChange={(e) => setSubTitle(e.target.value)}
                  />
                </div>

                {/* Add more basic information */}
                <div className="scp-extra-info" style={{ marginTop: '24px' }}>
                  <h4 className="scp-extra-heading">Add more basic information</h4>
                  <div className="scp-extra-fields-box">
                    {extraFields.map((field, index) => (
                      <div className="scp-extra-row" key={index}>
                        <div className="scp-field">
                          <label className="scp-label">Title</label>
                          <input
                            type="text"
                            className="scp-input"
                            placeholder="Write title name"
                            value={field.title}
                            onChange={(e) => updateExtraField(index, 'title', e.target.value)}
                          />
                        </div>
                        <div className="scp-field">
                          <label className="scp-label">Value</label>
                          <input
                            type="text"
                            className="scp-input"
                            placeholder="Write value"
                            value={field.value}
                            onChange={(e) => updateExtraField(index, 'value', e.target.value)}
                          />
                        </div>
                        <div className="scp-extra-delete-wrap">
                          <button type="button" className="scp-trash-btn" onClick={() => removeExtraField(index)}>
                            <svg width="28" height="30" viewBox="0 0 28 30" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <rect width="28" height="29.5" rx="4" fill="#FF0000" fillOpacity="0.2"/>
                              <path d="M22.25 8H18.5V7.25C18.5 6.653 18.263 6.081 17.841 5.659C17.419 5.237 16.847 5 16.25 5H11.75C11.153 5 10.581 5.237 10.159 5.659C9.737 6.081 9.5 6.653 9.5 7.25V8H5.75C5.551 8 5.36 8.079 5.22 8.22C5.079 8.36 5 8.551 5 8.75C5 8.949 5.079 9.14 5.22 9.28C5.36 9.421 5.551 9.5 5.75 9.5H6.5V23C6.5 23.398 6.658 23.779 6.939 24.061C7.221 24.342 7.602 24.5 8 24.5H20C20.398 24.5 20.779 24.342 21.061 24.061C21.342 23.779 21.5 23.398 21.5 23V9.5H22.25C22.449 9.5 22.64 9.421 22.78 9.28C22.921 9.14 23 8.949 23 8.75C23 8.551 22.921 8.36 22.78 8.22C22.64 8.079 22.449 8 22.25 8ZM11 7.25C11 7.051 11.079 6.86 11.22 6.72C11.36 6.579 11.551 6.5 11.75 6.5H16.25C16.449 6.5 16.64 6.579 16.78 6.72C16.921 6.86 17 7.051 17 7.25V8H11V7.25ZM20 23H8V9.5H20V23ZM12.5 13.25V19.25C12.5 19.449 12.421 19.64 12.28 19.78C12.14 19.921 11.949 20 11.75 20C11.551 20 11.36 19.921 11.22 19.78C11.079 19.64 11 19.449 11 19.25V13.25C11 13.051 11.079 12.86 11.22 12.72C11.36 12.579 11.551 12.5 11.75 12.5C11.949 12.5 12.14 12.579 12.28 12.72C12.421 12.86 12.5 13.051 12.5 13.25ZM17 13.25V19.25C17 19.449 16.921 19.64 16.78 19.78C16.64 19.921 16.449 20 16.25 20C16.051 20 15.86 19.921 15.72 19.78C15.579 19.64 15.5 19.449 15.5 19.25V13.25C15.5 13.051 15.579 12.86 15.72 12.72C15.86 12.579 16.051 12.5 16.25 12.5C16.449 12.5 16.64 12.579 16.78 12.72C16.921 12.86 17 13.051 17 13.25Z" fill="#FF0000"/>
                            </svg>
                          </button>
                        </div>
                      </div>
                    ))}
                    {/* Add New button */}
                    <button type="button" className="scp-add-new-btn" onClick={addExtraField}>
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M12 5V19" stroke="#1F396C" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M5 12H19" stroke="#1F396C" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                      <span>Add New</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* === Section: Image === */}
            <div className="scp-section">
              <h3 className="scp-section-heading">Image</h3>
              <div className="scp-card">
                {/* Project main image */}
                <div className="scp-image-block">
                  <h4 className="scp-image-block-title">Project main image</h4>
                  <div className="scp-image-block-content">
                    {/* File row */}
                    <FileRow
                      name={mainImageName}
                      file={mainImageFile}
                      existingUrl={editingProject?.image}
                      onDelete={() => { setMainImageFile(null); setMainImageName(''); }}
                      onChoose={() => mainImageRef.current?.click()}
                    />
                    <input
                      type="file"
                      ref={mainImageRef}
                      accept="image/*"
                      style={{ display: 'none' }}
                      onChange={handleMainImageSelect}
                    />
                    {/* Upload section */}
                    <UploadSection onUploadClick={() => mainImageRef.current?.click()} />
                  </div>
                </div>

                {/* Project main image (Maximum 10 image) */}
                <div className="scp-image-block" style={{ marginTop: '24px' }}>
                  <h4 className="scp-image-block-title">
                    <span>Project main image </span>
                    <span className="scp-image-block-subtitle">( Maximum 10 image)</span>
                  </h4>
                  <div className="scp-image-block-content">
                    {projectImages.map((img, i) => (
                      <div key={i}>
                        <FileRow
                          name={img.name}
                          file={img.file}
                          onDelete={() => removeProjectImage(i)}
                          onChoose={() => {
                            const input = document.createElement('input');
                            input.type = 'file';
                            input.accept = 'image/*';
                            input.onchange = (e) => handleProjectImageFile(i, e);
                            input.click();
                          }}
                        />
                      </div>
                    ))}
                    <input
                      type="file"
                      ref={projectUploadRef}
                      accept="image/*"
                      multiple
                      style={{ display: 'none' }}
                      onChange={handleBulkProjectUpload}
                    />
                    <UploadSection onUploadClick={() => projectUploadRef.current?.click()} />
                  </div>
                </div>

                {/* Project Gallery (Maximum 16 image) */}
                <div className="scp-image-block" style={{ marginTop: '24px' }}>
                  <h4 className="scp-image-block-title">
                    <span>Project Gallery</span>
                    <span className="scp-image-block-subtitle">( Maximum 16 image)</span>
                  </h4>
                  <div className="scp-image-block-content">
                    {galleryImages.map((img, i) => (
                      <div key={i}>
                        <FileRow
                          name={img.name}
                          file={img.file}
                          onDelete={() => removeGalleryImage(i)}
                          onChoose={() => {
                            const input = document.createElement('input');
                            input.type = 'file';
                            input.accept = 'image/*';
                            input.onchange = (e) => handleGalleryImageFile(i, e);
                            input.click();
                          }}
                        />
                      </div>
                    ))}
                    <input
                      type="file"
                      ref={galleryUploadRef}
                      accept="image/*"
                      multiple
                      style={{ display: 'none' }}
                      onChange={handleBulkGalleryUpload}
                    />
                    <UploadSection onUploadClick={() => galleryUploadRef.current?.click()} />
                  </div>
                </div>
              </div>
            </div>

            {/* Next Button */}
            <div className="scp-bottom-actions">
              <button type="button" className="scp-next-btn" onClick={handleNext} disabled={saving}>
                Next
              </button>
            </div>
          </div>
        )}

        {/* ===== STEP 2 ===== */}
        {wizardStep === 2 && (
          <div className="scp-form-sections">
            <div className="scp-section">
              <h3 className="scp-section-heading">Project Description</h3>
              <div className="scp-card">
                <div className="scp-field scp-field-full">
                  <label className="scp-label">Description</label>
                  <textarea
                    className="scp-textarea"
                    rows="10"
                    placeholder="Write detailed project description..."
                    value={subTitle}
                    onChange={(e) => setSubTitle(e.target.value)}
                  />
                </div>
              </div>
            </div>

            <div className="scp-bottom-actions scp-bottom-actions-dual">
              <button type="button" className="scp-back-btn" onClick={() => setWizardStep(1)}>Back</button>
              <button type="button" className="scp-next-btn" onClick={() => { setError(''); setWizardStep(3); }}>
                Next
              </button>
            </div>
          </div>
        )}

        {/* ===== STEP 3 (Publish) ===== */}
        {wizardStep === 3 && (
          <div className="scp-form-sections">
            <div className="scp-section">
              <h3 className="scp-section-heading">Review & Publish</h3>
              <div className="scp-card">
                <div className="scp-review-grid">
                  <div className="scp-review-item">
                    <span className="scp-review-label">Project Title</span>
                    <span className="scp-review-value">{projectTitle || '-'}</span>
                  </div>
                  <div className="scp-review-item">
                    <span className="scp-review-label">Sub Title</span>
                    <span className="scp-review-value">{subTitle || '-'}</span>
                  </div>
                  <div className="scp-review-item">
                    <span className="scp-review-label">Category</span>
                    <span className="scp-review-value">{category || '-'}</span>
                  </div>
                  <div className="scp-review-item">
                    <span className="scp-review-label">Sub Category</span>
                    <span className="scp-review-value">{subCategory || '-'}</span>
                  </div>
                  {extraFields.filter(f => f.title).map((f, i) => (
                    <div className="scp-review-item" key={i}>
                      <span className="scp-review-label">{f.title}</span>
                      <span className="scp-review-value">{f.value || '-'}</span>
                    </div>
                  ))}
                  <div className="scp-review-item">
                    <span className="scp-review-label">Main Image</span>
                    <span className="scp-review-value">{mainImageName || 'Not uploaded'}</span>
                  </div>
                  <div className="scp-review-item">
                    <span className="scp-review-label">Project Images</span>
                    <span className="scp-review-value">{projectImages.filter(i => i.name).length} uploaded</span>
                  </div>
                  <div className="scp-review-item">
                    <span className="scp-review-label">Gallery Images</span>
                    <span className="scp-review-value">{galleryImages.filter(i => i.name).length} uploaded</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="scp-bottom-actions scp-bottom-actions-dual">
              <button type="button" className="scp-back-btn" onClick={() => setWizardStep(2)}>Back</button>
              <button type="button" className="scp-next-btn" onClick={handleSubmit} disabled={saving}>
                {saving ? 'Publishing...' : editingProject ? 'Update Project' : 'Publish Project'}
              </button>
            </div>
          </div>
        )}
      </div>
    </SpecialistDashboardLayout>
  );
};

export default SpecialistCreateProjectPage;
